import React, { useState, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash } from "@fortawesome/free-solid-svg-icons"
import { useSelector } from "react-redux"
import { selectUser } from "../../../../redux/store/auth/authSlice"
import { Note } from "../../../../types/CampaignDatas"

interface CampaignNotesModalProps {
  isOpen: boolean
  onClose: () => void
  campaignId: string
  notes: Note[]
  onAddNote:
    | ((campaignId: string, note: string) => Promise<Note[] | undefined>)
    | ((note: string) => Promise<Note[] | undefined>)
  onDeleteNote: (campaignId: string, index: number) => void
}

const CampaignNotesModal: React.FC<CampaignNotesModalProps> = ({
  isOpen,
  onClose,
  campaignId,
  notes,
  onAddNote,
  onDeleteNote,
}) => {
  const [newNote, setNewNote] = useState("")
  const [localNotes, setLocalNotes] = useState<Note[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const userEmail = useSelector(selectUser)?.replace(/"/g, "") || ""

  useEffect(() => {
    if (isOpen) {
      setLocalNotes(notes || [])
      setNewNote("")
    }
  }, [isOpen, notes])

  const noteCount = localNotes?.filter((n) => n.note && n.note !== "").length || 0

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      return
    }

    const newNoteObj: Note = {
      note: newNote.trim(),
      author: userEmail,
      note_time: new Date(),
    }
    setLocalNotes((prev) => [newNoteObj, ...prev])

    setIsSaving(true)
    try {
      const serverNotes =
        onAddNote.length === 2
          ? await (onAddNote as (campaignId: string, note: string) => Promise<Note[] | undefined>)(
              campaignId,
              newNote.trim(),
            )
          : await (onAddNote as (note: string) => Promise<Note[] | undefined>)(newNote.trim())

      if (serverNotes) {
        setLocalNotes(serverNotes)
      }
      setNewNote("")
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleAddNote()
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed right-0 top-0 h-full z-50">
      <div className="bg-white h-full w-96 shadow-lg flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold">Kampanya Notları</h2>
              <p className="text-sm opacity-90 mt-1">Kampanya ID: {campaignId}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 p-4 overflow-y-auto">
          <div className="mb-6">
            {localNotes && localNotes.length > 0 ? (
              <div className="space-y-3">
                {localNotes
                  .filter((note) => note.note && note.note !== "")
                  .map((note, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-gray-800 text-sm mb-2">{note.note}</p>
                          <div className="flex items-center text-xs text-gray-500 mt-2">
                            <span className="font-semibold">{note.author}</span>
                            <span className="mx-1">•</span>
                            <span>{new Date(note.note_time).toLocaleString("tr-TR")}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            onDeleteNote(campaignId, index)
                            setLocalNotes((prev) => prev.filter((_, i) => i !== index))
                          }}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Henüz not eklenmemiş</p>
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Yeni Not Ekle</h3>
            <div className="flex flex-col gap-2">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Notunuzu buraya yazın..."
                className="w-full p-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>
          </div>
        </div>

        <div className="border-t p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">Toplam: {noteCount} not</div>
            <button
              onClick={handleAddNote}
              disabled={!newNote.trim() || isSaving}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>{isSaving ? "Kaydediliyor..." : "Kaydet"}</span>
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CampaignNotesModal
